/**
 * alarm-summary.tsx — live human-readable sentence composed from the current
 * form state. Example:
 *   "Alert on Telegram + Email when status_code ≥ 500 for 3 consecutive failures."
 *
 * Shows nothing (empty fragment) when there's insufficient data to summarise.
 */
import type { AlarmSeverity } from '@/api/healthcheck';
import type { ConditionRow } from './condition-serializer';

interface Props {
  rows: ConditionRow[];
  mode: 'and' | 'or';
  severity: AlarmSeverity;
  consecutiveFailures: number;
  channelNames: string[];
}

const OPERATOR_HUMAN: Record<string, string> = {
  $eq: '=',
  $neq: '≠',
  $gt: '>',
  $gte: '≥',
  $lt: '<',
  $lte: '≤',
  $in: 'in',
  $nin: 'not in',
  $contains: 'contains',
  $prefix: 'starts with',
  $matches: 'matches',
};

function rowText(row: ConditionRow): string {
  if (!row.field || !row.operator) return '';
  const op = OPERATOR_HUMAN[row.operator] ?? row.operator;
  const val = Array.isArray(row.value)
    ? `[${(row.value as unknown[]).join(', ')}]`
    : String(row.value ?? '');
  return `${row.field} ${op} ${val}`;
}

export function AlarmSummary({ rows, mode, severity, consecutiveFailures, channelNames }: Props) {
  const validRows = rows.filter((r) => r.field && r.operator && r.value !== '');
  if (validRows.length === 0) return null;

  const conditionText =
    validRows.length === 1
      ? rowText(validRows[0])
      : validRows.map(rowText).join(mode === 'or' ? ' OR ' : ' AND ');

  const channelText =
    channelNames.length === 0
      ? 'no channels'
      : channelNames.length <= 3
        ? channelNames.join(' + ')
        : `${channelNames.slice(0, 2).join(', ')} +${channelNames.length - 2} more`;

  const failuresText =
    consecutiveFailures === 1
      ? '1 consecutive failure'
      : `${consecutiveFailures} consecutive failures`;

  return (
    <div
      style={{
        padding: 'var(--vn-space-3)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-subtle)',
        border: '1px solid var(--vn-border-subtle)',
        fontSize: 'var(--vn-font-size-sm)',
        color: 'var(--vn-fg-muted)',
        lineHeight: 1.5,
      }}
    >
      <strong style={{ color: 'var(--vn-fg-default)' }}>Preview: </strong>
      Alert on{' '}
      <strong style={{ color: 'var(--vn-fg-default)' }}>{channelText}</strong> with{' '}
      <strong style={{ color: 'var(--vn-fg-default)' }}>{severity}</strong> severity when{' '}
      <code
        style={{
          background: 'var(--vn-bg-hover)',
          padding: '1px 4px',
          borderRadius: 'var(--vn-radius-sm)',
          fontFamily: 'var(--vn-font-mono)',
          color: 'var(--vn-fg-default)',
        }}
      >
        {conditionText}
      </code>{' '}
      for{' '}
      <strong style={{ color: 'var(--vn-fg-default)' }}>{failuresText}</strong>.
    </div>
  );
}
